import { TGetMeetingTimersArgs, TimeUnit } from '@src/types';

type TMeetingTimers = {
  startedAt: number;
  endAt: number;
  warnAt: number;
};

export const convertToMilliseconds = (duration: number, unit: TimeUnit = 'minutes') => {
  const n = Number(duration) || 0;
  if (n <= 0) return 0;
  switch (unit) {
    case 'seconds':
      return n * 1000;
    case 'minutes':
      return n * 60 * 1000;
    case 'hours':
      return n * 60 * 60 * 1000;
    case 'days':
      return n * 24 * 60 * 60 * 1000;
    default:
      return 0;
  }
};

const normalizeUnit = (raw?: string): TimeUnit => {
  const u = String(raw ?? 'minutes')
    .trim()
    .toLowerCase();
  if (['s', 'sec', 'secs', 'second', 'seconds'].includes(u)) return 'seconds';
  if (['m', 'min', 'mins', 'minute', 'minutes'].includes(u)) return 'minutes';
  if (['h', 'hr', 'hrs', 'hour', 'hours'].includes(u)) return 'hours';
  if (['d', 'day', 'days'].includes(u)) return 'days';
  return ['seconds', 'minutes', 'hours', 'days'].includes(u) ? (u as TimeUnit) : 'minutes';
};

const convertToTimestampMs = (value: number | string | Date) => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return Number.isFinite(value) ? value : Date.now();
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : Date.now();
  }
  return Date.now();
};

export const getEventTime = ({
  startTime,
  duration,
  unit = 'minutes',
  warnLeadMs = convertToMilliseconds(5, 'minutes'),
}: TGetMeetingTimersArgs): TMeetingTimers | null => {
  const durationMs = convertToMilliseconds(Number(duration) || 0, normalizeUnit(unit));
  if (!durationMs) return null;

  let startedAt = convertToTimestampMs(startTime ?? Date.now());
  const now = Date.now();

  if (startedAt > now) {
    startedAt = now;
  }

  const endAt = startedAt + durationMs;
  let warnAt = endAt - (warnLeadMs || 0);

  if (warnAt < now) {
    warnAt = now;
  }

  return { startedAt, endAt, warnAt };
};
